import * as React from "react";
import { useRef, useState, useMemo, useEffect, useCallback } from "react";
import { Box, Chip, Typography, Collapse } from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Cancel } from "@mui/icons-material";
import {
  StyledCheckbox,
  StyledMenuItemContainer,
  StyledMenuListItemText,
  StyledSubMenuItemContainer,
  HtmlTooltip,
} from "../StyledComponents/styled";
import { erase_duplicates } from "../../../api_calls/utils";
import SelectFieldShell from "./SelectFieldShell";
import useSelectDropdown from "./useSelectDropdown";
import { estimateOptionsContentWidth } from "./selectorOptionUtils";
import { SELECT_COLOR, SELECT_HIGHLIGHT_COLOR, SELECT_MOTION_MS } from "./selectConstants";

// Child rows sit one indent level in (StyledSubMenuItemContainer).
const GROUP_OPTION_INDENT_PX = 25;

export default function GroupedMultiSelect({
  label,
  defaultVal,
  elemId,
  options,
  onChangeCall,
  fieldError,
  enableFullWidth,
  selectionCheck,
  onBlurCall,
  readOnlyFlag,
  groupParam,
  disabledIfSelectedOption,
  showGroupNames,
  disableSearch,
  showSelectionSummary,
  additionalIconWithTooltip,
  additionalIconMarkup,
  onClearCall,
  usedFor,
  // Role pickers pass false. Filters and store dropdowns keep Select all.
  showSelectAll = true,
}) {
  const itemRefs = useRef({});
  const optionRefs = useRef({});
  const [selectedValues, setSelectedValues] = useState(defaultVal);
  const [expanded, setExpanded] = useState({});
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [instanceId] = useState(
    () => elemId || `grouped-multi-select-${Math.random().toString(36).substr(2, 9)}`
  );

  const isDisabled = typeof readOnlyFlag !== "undefined" ? readOnlyFlag : false;
  const showSearch = typeof disableSearch !== "undefined" ? !disableSearch : true;
  const selectedValueCount = selectedValues?.length ?? 0;

  // onBlurCall fires when the dropdown closes (click-away, Escape, or re-click).
  const handleDropdownClosed = useCallback(() => {
    setHighlightedIndex(-1);
    onBlurCall?.();
  }, [onBlurCall]);

  const dropdown = useSelectDropdown({
    instanceId,
    showSearch,
    onClosed: handleDropdownClosed,
  });
  const {
    open,
    setOpen,
    searchTerm,
    isFocused,
    paperRef,
    close: closeDropdown,
  } = dropdown;

  const close = useCallback(
    (shouldRefocus = false) =>
      closeDropdown({ clearSearch: true, refocus: shouldRefocus }),
    [closeDropdown]
  );

  const isFilter = usedFor === "filter";
  // Filter drawers float the label even when nothing is selected, so "All Results" fits.
  const shrink = isFocused || open || selectedValueCount > 0 || isFilter;

  useEffect(() => {
    setSelectedValues(defaultVal);
  }, [defaultVal]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchTerm]);

  const isSelected = useCallback(
    (option) => selectedValues.some((val) => selectionCheck(option, val)),
    [selectedValues, selectionCheck]
  );

  const isOptionDisabled = useCallback(
    (option) => {
      if (!disabledIfSelectedOption || disabledIfSelectedOption.length === 0) return false;

      const hasOtherSelected = selectedValues?.some(
        (selectedOption) =>
          !disabledIfSelectedOption?.some(
            (disabledOption) => disabledOption === selectedOption.label
          )
      );

      const optionIsDisabled =
        hasOtherSelected && disabledIfSelectedOption.includes(option.label)
          ? true
          : disabledIfSelectedOption?.some((disabledOption) =>
              selectedValues.some(
                (selectedOption) =>
                  selectedOption.label === disabledOption &&
                  option.label !== disabledOption
              )
            );

      return optionIsDisabled;
    },
    [disabledIfSelectedOption, selectedValues]
  );

  // Stays open after toggle — unlike GroupedSingle, which closes on select.
  const handleSelect = useCallback((option) => {
    if (isDisabled) return;
    if (isOptionDisabled(option)) return;

    const alreadySelected = isSelected(option);
    const newSelection = alreadySelected
      ? selectedValues.filter((val) => !selectionCheck(option, val))
      : [...selectedValues, option];

    setSelectedValues(newSelection);
    onChangeCall(newSelection);
  }, [isDisabled, isOptionDisabled, isSelected, selectedValues, selectionCheck, onChangeCall]);

  const handleRemove = (optionToRemove) => {
    if (isDisabled) return;

    const newSelection = selectedValues.filter(
      (val) => !selectionCheck(optionToRemove, val)
    );
    setSelectedValues(newSelection);
    onChangeCall(newSelection);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    if (isDisabled) return;

    setSelectedValues([]);
    onChangeCall([]);
    // Capacity Planning writes an empty store list here. onChange alone only updates local state.
    if (onClearCall) onClearCall();
  };

  const handleCategoryToggle = useCallback(
    (groupName) => {
      const wasExpanded = expanded[groupName];
      setExpanded((prev) => ({ ...prev, [groupName]: !prev[groupName] }));
      setHighlightedIndex(-1);

      if (!wasExpanded) {
        setTimeout(() => {
          const item = itemRefs.current[groupName];
          const container = paperRef.current;

          if (!item || !container) return;

          const itemRect = item.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();

          if (itemRect.bottom >= containerRect.bottom) {
            const scrollOffset = itemRect.top - containerRect.top - 8;
            container.scrollTo({
              top: container.scrollTop + scrollOffset,
              behavior: "smooth",
            });
          }
        }, 300);
      }
    },
    [expanded, paperRef]
  );

  const handleCategorySelect = (groupOptions, allSelected, e) => {
    e.stopPropagation();
    if (isDisabled) return;

    const selectableGroupOptions = groupOptions.filter((opt) => !isOptionDisabled(opt));
    if (selectableGroupOptions.length === 0) return;

    let newSelection;
    if (allSelected) {
      newSelection = selectedValues.filter(
        (val) => !selectableGroupOptions.some((opt) => selectionCheck(opt, val))
      );
    } else {
      const optionsToAdd = selectableGroupOptions.filter((opt) => !isSelected(opt));
      newSelection = [...selectedValues, ...optionsToAdd];
    }

    setSelectedValues(newSelection);
    onChangeCall(newSelection);
  };

  const uniqueOptions = useMemo(() => erase_duplicates(options), [options]);

  const allOptionsSelected = useMemo(() => {
    if (uniqueOptions.length === 0) return false;
    return uniqueOptions.every((opt) => isSelected(opt));
  }, [uniqueOptions, isSelected]);

  const groupedCategories = useMemo(() => {
    const groups = {};

    uniqueOptions.forEach((option) => {
      const groupName = option[groupParam] || "Other";
      if (!groups[groupName]) groups[groupName] = [];
      groups[groupName].push(option);
    });

    const sortedGroupNames = Object.keys(groups).sort((a, b) => {
      const aNum = parseInt(a.split("-")[0], 10);
      const bNum = parseInt(b.split("-")[0], 10);

      const aHasNum = !Number.isNaN(aNum);
      const bHasNum = !Number.isNaN(bNum);

      if (aHasNum && bHasNum) return aNum - bNum;
      return a.localeCompare(b, undefined, { numeric: true });
    });

    return sortedGroupNames.map((groupName) => ({
      groupName,
      options: groups[groupName].sort((a, b) =>
        String(a.label).localeCompare(String(b.label), undefined, { numeric: true })
      ),
    }));
  }, [uniqueOptions, groupParam]);

  const searchValue = searchTerm.trim().toLowerCase();

  const filteredCategories = useMemo(() => {
    if (!showSearch || !searchValue) return groupedCategories;

    return groupedCategories
      .map((cat) => {
        const lowerCaseGroupName = cat.groupName.toLowerCase();

        if (lowerCaseGroupName.includes(searchValue)) return cat;

        const filteredOptions = cat.options.filter((opt) => {
          const optLabel = String(opt?.label ?? "").toLowerCase();
          return optLabel.includes(searchValue);
        });

        if (filteredOptions.length > 0) return { ...cat, options: filteredOptions };

        return null;
      })
      .filter((cat) => cat !== null);
  }, [groupedCategories, searchValue, showSearch]);

  const isSearchActive = showSearch && searchTerm.trim().length > 0;

  // Filter bars hide Select All until the user searches. Forms and store filters keep it.
  // showSelectAll false hides it entirely, including "Select All Results" while searching.
  const selectAllVisible = showSelectAll === true && (usedFor !== "filter" || isSearchActive);

  // Every option the user can still pick. Disabled rows stay out of Select All.
  const selectableOptions = useMemo(
    () => uniqueOptions.filter((opt) => !isOptionDisabled(opt)),
    [uniqueOptions, isOptionDisabled]
  );

  const allSelectableSelected = useMemo(() => {
    if (selectableOptions.length === 0) return false;
    return selectableOptions.every((opt) => isSelected(opt));
  }, [selectableOptions, isSelected]);

  const someSelectableSelected = useMemo(() => {
    const count = selectableOptions.filter((opt) => isSelected(opt)).length;
    return count > 0 && !allSelectableSelected;
  }, [selectableOptions, isSelected, allSelectableSelected]);

  // Select All toggles every selectable option, including stores inside collapsed groups.
  const handleSelectAll = useCallback(() => {
    if (isDisabled) return;

    const allSel =
      selectableOptions.length > 0 &&
      selectableOptions.every((opt) => isSelected(opt));

    let newSelection;
    if (allSel) {
      newSelection = selectedValues.filter(
        (val) => !selectableOptions.some((opt) => selectionCheck(opt, val))
      );
    } else {
      const toAdd = selectableOptions.filter((opt) => !isSelected(opt));
      newSelection = [...selectedValues, ...toAdd];
    }

    setSelectedValues(newSelection);
    onChangeCall(newSelection);
  }, [isDisabled, selectableOptions, isSelected, selectedValues, selectionCheck, onChangeCall]);

  // Options still visible after search, used for Select All Results.
  const filteredFlatOptions = useMemo(
    () => filteredCategories.flatMap((cat) => cat.options),
    [filteredCategories]
  );

  const selectableFilteredOptions = useMemo(
    () => filteredFlatOptions.filter((opt) => !isOptionDisabled(opt)),
    [filteredFlatOptions, isOptionDisabled]
  );

  const allFilteredSelected = useMemo(() => {
    if (selectableFilteredOptions.length === 0) return false;
    return selectableFilteredOptions.every((opt) => isSelected(opt));
  }, [selectableFilteredOptions, isSelected]);

  const someFilteredSelected = useMemo(() => {
    const count = selectableFilteredOptions.filter((opt) => isSelected(opt)).length;
    return count > 0 && !allFilteredSelected;
  }, [selectableFilteredOptions, isSelected, allFilteredSelected]);

  const handleSelectAllFiltered = useCallback(() => {
    if (isDisabled) return;

    const allSel =
      selectableFilteredOptions.length > 0 &&
      selectableFilteredOptions.every((opt) => isSelected(opt));

    let newSelection;
    if (allSel) {
      newSelection = selectedValues.filter(
        (val) => !selectableFilteredOptions.some((opt) => selectionCheck(opt, val))
      );
    } else {
      const toAdd = selectableFilteredOptions.filter((opt) => !isSelected(opt));
      newSelection = [...selectedValues, ...toAdd];
    }

    setSelectedValues(newSelection);
    onChangeCall(newSelection);
  }, [isDisabled, selectableFilteredOptions, isSelected, selectedValues, selectionCheck, onChangeCall]);

  const flatOptionsList = useMemo(() => {
    const list = [];

    // Index 0 is the Select All row. While searching it selects only the visible matches.
    if (selectAllVisible) {
      list.push({ type: "select-all" });
    }

    if (showGroupNames) {
      filteredCategories.forEach((cat) => {
        list.push({ type: "group", groupName: cat.groupName, data: cat });
        if (expanded[cat.groupName]) {
          cat.options.forEach((opt) => {
            list.push({ type: "option", option: opt, groupName: cat.groupName });
          });
        }
      });
    } else {
      filteredCategories.flatMap((cat) =>
        cat.options.map((opt) => ({ type: "option", option: opt }))
      ).forEach((item) => list.push(item));
    }

    return list;
  }, [filteredCategories, expanded, showGroupNames, selectAllVisible]);

  const scrollHighlightedIntoView = useCallback((index) => {
    const element = optionRefs.current[`item-${index}`];
    if (element) element.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, []);

  const handleListKeyDown = useCallback(
    (e) => {
      if (!open) return;

      const totalCount = flatOptionsList.length;
      if (totalCount === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          e.stopPropagation();
          setHighlightedIndex((prev) => {
            const next = prev < totalCount - 1 ? prev + 1 : 0;
            setTimeout(() => scrollHighlightedIntoView(next), 0);
            return next;
          });
          break;

        case "ArrowUp":
          e.preventDefault();
          e.stopPropagation();
          setHighlightedIndex((prev) => {
            const next = prev > 0 ? prev - 1 : totalCount - 1;
            setTimeout(() => scrollHighlightedIntoView(next), 0);
            return next;
          });
          break;

        case "Enter":
          e.preventDefault();
          e.stopPropagation();

          if (highlightedIndex < 0 || highlightedIndex >= flatOptionsList.length) return;

          {
            const item = flatOptionsList[highlightedIndex];
            if (item.type === "select-all") {
              if (isSearchActive) handleSelectAllFiltered();
              else handleSelectAll();
            } else if (item.type === "group") {
              handleCategoryToggle(item.groupName);
            } else if (item.type === "option" && !isOptionDisabled(item.option)) {
              handleSelect(item.option);
            }
          }
          break;

        case " ":
          if (e.target.tagName === "INPUT") return;
          e.preventDefault();
          e.stopPropagation();

          if (highlightedIndex < 0 || highlightedIndex >= flatOptionsList.length) return;

          {
            const spaceItem = flatOptionsList[highlightedIndex];
            if (spaceItem.type === "select-all") {
              if (isSearchActive) handleSelectAllFiltered();
              else handleSelectAll();
            } else if (spaceItem.type === "option" && !isOptionDisabled(spaceItem.option)) {
              handleSelect(spaceItem.option);
            }
          }
          break;

        case "Escape":
          e.preventDefault();
          e.stopPropagation();
          close();
          break;

        default:
          break;
      }
    },
    [
      open,
      flatOptionsList,
      highlightedIndex,
      close,
      isOptionDisabled,
      scrollHighlightedIntoView,
      handleSelect,
      handleSelectAll,
      handleSelectAllFiltered,
      isSearchActive,
      handleCategoryToggle,
    ]
  );

  // Reported to the shell, which decides the final panel width. Group headers sit
  // flush left while their options are indented, so both are measured.
  const dropdownContentWidth = useMemo(() => {
    const groupHeaderWidth = estimateOptionsContentWidth(
      groupedCategories.map((category) => category.groupName)
    );
    const optionRowWidth = estimateOptionsContentWidth(
      uniqueOptions.map((option) => option?.label),
      GROUP_OPTION_INDENT_PX
    );

    return Math.max(groupHeaderWidth, optionRowWidth);
  }, [groupedCategories, uniqueOptions]);

  const renderDisplayContent = () => {
    if (selectedValueCount === 0) {
      // Empty filter means every result, same as the flat MultiSelect.
      return isFilter ? (
        <Typography variant="body2" sx={{ color: SELECT_COLOR }}>
          All Results
        </Typography>
      ) : null;
    }

    if (showSelectionSummary === true && allOptionsSelected === true) {
      return (
        <Typography variant="body2" sx={{ color: SELECT_COLOR }}>
          All
        </Typography>
      );
    }

    if (showSelectionSummary === true) {
      return (
        <Typography variant="body2" sx={{ color: SELECT_COLOR }}>
          {selectedValueCount + " Selected"}
        </Typography>
      );
    }

    return selectedValues.map((option, index) => (
      <Chip
        key={option?.label ?? index}
        size="small"
        label={option.label}
        onClick={(e) => e.stopPropagation()}
        onDelete={
          isDisabled
            ? undefined
            : (e) => {
                e.stopPropagation();
                handleRemove(option);
              }
        }
        deleteIcon={<Cancel sx={{ color: SELECT_COLOR }} />}
        sx={{
          flex: "0 0 auto",
          backgroundColor: SELECT_COLOR,
          color: "#fff",
          fontSize: "12px",
          height: 24,
          "& .MuiChip-deleteIcon": { color: "#fff" },
        }}
      />
    ));
  };

  // flatIndex maps each visible row to keyboard navigation.
  // Index 0 is Select All when that row is shown.
  let flatIndex = selectAllVisible ? 0 : -1;

  return (
    <SelectFieldShell
      dropdown={dropdown}
      elemId={elemId}
      label={label}
      shrink={shrink}
      disabled={isDisabled}
      enableFullWidth={enableFullWidth}
      canClear={selectedValueCount > 0}
      onClear={handleClear}
      fieldError={fieldError}
      showSearch={showSearch}
      preserveListFocus
      valueContainerSx={{
        flexWrap: "wrap",
        overflowX: "auto",
        overflowY: "hidden",
        gap: 0.5,
        whiteSpace: "nowrap",
        "&::-webkit-scrollbar": { height: 6 },
      }}
      onAnchorClick={() => {
        // Re-clicking the anchor while open closes (and fires onBlurCall via close).
        if (isDisabled) return;
        if (open) {
          close(false);
          return;
        }
        setOpen(true);
      }}
      onAnchorKeyDown={(event) => {
        if (isDisabled) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          setOpen(true);
        }
        if (event.key === "Escape") close();
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault();
          if (!open) setOpen(true);
        }
      }}
      onListKeyDown={handleListKeyDown}
      contentWidth={dropdownContentWidth}
      endAdornment={
        additionalIconWithTooltip === true && typeof additionalIconMarkup === "function" ? (
          <Box
            component="span"
            sx={{ display: "inline-flex", alignItems: "center" }}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
          >
            {additionalIconMarkup(setOpen)}
          </Box>
        ) : null
      }
      valueDisplay={
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
            width: "100%",
          }}
          onClick={(e) => {
            // Clicking chips/summary opens without toggling closed via the anchor.
            e.stopPropagation();
            if (!isDisabled) setOpen(true);
          }}
        >
          {renderDisplayContent()}
        </Box>
      }
    >
      {selectAllVisible && (
        <StyledMenuItemContainer
          ref={(el) => {
            optionRefs.current["item-0"] = el;
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (isSearchActive) handleSelectAllFiltered();
            else handleSelectAll();
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseEnter={() => setHighlightedIndex(0)}
          sx={{
            display: "flex",
            alignItems: "center",
            backgroundColor:
              highlightedIndex === 0 ? SELECT_HIGHLIGHT_COLOR : "transparent",
          }}
        >
          <StyledCheckbox
            checked={isSearchActive ? allFilteredSelected : allSelectableSelected}
            indeterminate={isSearchActive ? someFilteredSelected : someSelectableSelected}
            onMouseDown={(e) => e.stopPropagation()}
          />
          <StyledMenuListItemText
            primary={isSearchActive ? "Select All Results" : "Select All"}
          />
        </StyledMenuItemContainer>
      )}

      {showGroupNames === true ? (
        filteredCategories.map((cat) => {
          const { groupName, options: groupOptions } = cat;
          const isExpanded = !!expanded[groupName];

          const fullGroupOptions =
            groupedCategories.find((g) => g.groupName === groupName)?.options || [];

          const selectableGroupOptions = fullGroupOptions.filter(
            (opt) => !isOptionDisabled(opt)
          );
          const selectedCount = selectableGroupOptions.filter((opt) =>
            isSelected(opt)
          ).length;
          const allSelected =
            selectableGroupOptions.length > 0 &&
            selectedCount === selectableGroupOptions.length;
          const someSelected = selectedCount > 0 && !allSelected;

          flatIndex++;
          const groupIndex = flatIndex;

          return (
            <Box
              key={groupName}
              ref={(el) => {
                itemRefs.current[groupName] = el;
              }}
              sx={{ width: "100%" }}
              onClick={(e) => e.stopPropagation()}
            >
              <StyledMenuItemContainer
                ref={(el) => {
                  optionRefs.current[`item-${groupIndex}`] = el;
                }}
                onClick={() => handleCategoryToggle(groupName)}
                onMouseEnter={() => setHighlightedIndex(groupIndex)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor:
                    highlightedIndex === groupIndex
                      ? SELECT_HIGHLIGHT_COLOR
                      : "transparent",
                }}
              >
                <StyledCheckbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onClick={(e) =>
                    handleCategorySelect(fullGroupOptions, allSelected, e)
                  }
                  onMouseDown={(e) => e.stopPropagation()}
                />
                <StyledMenuListItemText primary={groupName} />
                {isExpanded ? (
                  <ExpandLessIcon fontSize="small" sx={{ ml: 1 }} />
                ) : (
                  <ExpandMoreIcon fontSize="small" sx={{ ml: 1 }} />
                )}
              </StyledMenuItemContainer>

              <Collapse in={isExpanded} timeout={SELECT_MOTION_MS} unmountOnExit>
                {groupOptions.map((option, index) => {
                  const optionKey = option?.label ?? index;
                  const checked = isSelected(option);
                  const optionDisabled = isOptionDisabled(option);

                  flatIndex++;
                  const optIndex = flatIndex;

                  const optionContent = (
                    <StyledSubMenuItemContainer
                      ref={(el) => {
                        optionRefs.current[`item-${optIndex}`] = el;
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!optionDisabled) handleSelect(option);
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      onMouseEnter={() => setHighlightedIndex(optIndex)}
                      sx={{
                        pl: 5,
                        display: "flex",
                        alignItems: "center",
                        opacity: optionDisabled ? 0.5 : 1,
                        cursor: optionDisabled ? "default" : "pointer",
                        backgroundColor:
                          highlightedIndex === optIndex
                            ? SELECT_HIGHLIGHT_COLOR
                            : "transparent",
                      }}
                    >
                      <StyledCheckbox
                        checked={checked}
                        disabled={optionDisabled}
                        onMouseDown={(e) => e.stopPropagation()}
                      />
                      <StyledMenuListItemText primary={option.label} />
                    </StyledSubMenuItemContainer>
                  );

                  if (optionDisabled) {
                    return (
                      <HtmlTooltip
                        key={optionKey}
                        title="This option is unavailable"
                        arrow
                      >
                        <Box>{optionContent}</Box>
                      </HtmlTooltip>
                    );
                  }

                  return (
                    <React.Fragment key={optionKey}>
                      {optionContent}
                    </React.Fragment>
                  );
                })}
              </Collapse>
            </Box>
          );
        })
      ) : (
        filteredCategories.flatMap((cat) =>
          cat.options.map((option, index) => {
            const optionKey = option?.label ?? index;
            const checked = isSelected(option);
            const optionDisabled = isOptionDisabled(option);

            flatIndex++;
            const optIndex = flatIndex;

            const optionContent = (
              <StyledMenuItemContainer
                ref={(el) => {
                  optionRefs.current[`item-${optIndex}`] = el;
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!optionDisabled) handleSelect(option);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseEnter={() => setHighlightedIndex(optIndex)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  opacity: optionDisabled ? 0.5 : 1,
                  cursor: optionDisabled ? "default" : "pointer",
                  backgroundColor:
                    highlightedIndex === optIndex
                      ? SELECT_HIGHLIGHT_COLOR
                      : "transparent",
                }}
              >
                <StyledCheckbox
                  checked={checked}
                  disabled={optionDisabled}
                  onMouseDown={(e) => e.stopPropagation()}
                />
                <Typography
                  variant="body2"
                  className="selectorOptions"
                  sx={{
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    color: optionDisabled ? "#9e9e9e" : "inherit",
                  }}
                >
                  {option.label}
                </Typography>
              </StyledMenuItemContainer>
            );

            if (optionDisabled) {
              return (
                <HtmlTooltip
                  key={optionKey}
                  title="This option is unavailable"
                  arrow
                >
                  <Box>{optionContent}</Box>
                </HtmlTooltip>
              );
            }

            return (
              <React.Fragment key={optionKey}>
                {optionContent}
              </React.Fragment>
            );
          })
        )
      )}

      {filteredCategories.length === 0 && (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Typography variant="body2" color="textSecondary">
            No options found
          </Typography>
        </Box>
      )}
    </SelectFieldShell>
  );
}
